import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useVault } from '../contexts/VaultContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, ShieldCheck, Volume2, Download, Upload, Image, File, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import EncryptionBadge from '../components/EncryptionBadge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const { t, speak } = useLanguage();
  const { api, isUnlocked } = useVault();
  const [entries, setEntries] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [selectedReportId, setSelectedReportId] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    report_type: '',
    report_name: '',
    report_date: '',
    lab_name: '',
    findings: '',
    file_url: ''
  });

  const reportTypes = [
    { value: 'blood_test', label: t('bloodTest') },
    { value: 'xray', label: t('xray') },
    { value: 'mri', label: t('mri') },
    { value: 'ct_scan', label: t('ctScan') },
    { value: 'ultrasound', label: t('ultrasound') },
    { value: 'other', label: t('other') },
  ];

  useEffect(() => {
    if (isUnlocked) {
      fetchEntries();
      fetchFiles();
    }
  }, [isUnlocked]);

  const fetchEntries = async () => {
    try {
      // Use encrypted API
      const data = await api.getReports();
      setEntries(data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/files/user/list`);
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load files:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use encrypted API
      await api.createReport(formData);
      toast.success(t('recordAdded'));
      setDialogOpen(false);
      setFormData({ report_type: '', report_name: '', report_date: '', lab_name: '', findings: '', file_url: '' });
      fetchEntries();
    } catch (error) {
      toast.error('Failed to add report');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('File type not allowed. Use JPG, PNG, PDF, or WEBP');
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('description', uploadDescription);
    if (selectedReportId) {
      formData.append('report_id', selectedReportId);
    }

    try {
      const response = await axios.post(`${API_URL}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded and encrypted successfully!');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadDescription('');
      setSelectedReportId('');
      fetchFiles();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/files/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API_URL}/files/${fileId}`);
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.deleteReport(id);
      toast.success(t('recordDeleted'));
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getReportTypeLabel = (type) => {
    const found = reportTypes.find(rt => rt.value === type);
    return found ? found.label : type;
  };

  const getReportIcon = (type) => {
    const colors = {
      blood_test: 'bg-red-100 text-red-600',
      xray: 'bg-blue-100 text-blue-600',
      mri: 'bg-purple-100 text-purple-600',
      ct_scan: 'bg-orange-100 text-orange-600',
      ultrasound: 'bg-green-100 text-green-600',
      other: 'bg-slate-100 text-slate-600',
    };
    return colors[type] || colors.other;
  };

  const getFileForReport = (reportId) => {
    return files.find(f => f.report_id === reportId);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-green-100">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('reports')}</h1>
              <div className="flex items-center gap-2">
                <p className="text-slate-500">{entries.length} reports, {files.length} files</p>
                <EncryptionBadge variant="compact" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Upload File Button */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#0D2340] text-[#0D2340]" data-testid="upload-file-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Scan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                    <Lock className="h-5 w-5 text-[#0D2340]" />
                    Upload Encrypted File
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-[#0D2340]/5 p-3 rounded-lg text-sm">
                    <p className="font-medium text-[#0D2340]">🔐 AES-256 Encryption</p>
                    <p className="text-slate-600">Your file will be encrypted before storage. Only you can decrypt it.</p>
                  </div>
                  
                  <div>
                    <Label>Select File</Label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      accept="image/jpeg,image/png,image/webp,application/pdf,image/heic,image/heif"
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-24 border-dashed border-2 flex-col gap-2"
                    >
                      {selectedFile ? (
                        <>
                          <File className="h-8 w-8 text-[#0D2340]" />
                          <span className="text-sm">{selectedFile.name}</span>
                          <span className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-slate-400" />
                          <span className="text-slate-500">Click to select file</span>
                          <span className="text-xs text-slate-400">JPG, PNG, PDF, WEBP (max 10MB)</span>
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Link to Report (optional)</Label>
                    <Select value={selectedReportId} onValueChange={setSelectedReportId}>
                      <SelectTrigger data-testid="link-report-select">
                        <SelectValue placeholder="Select a report to link" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {entries.map((entry) => (
                          <SelectItem key={entry.id} value={entry.id}>
                            {entry.report_name} ({entry.report_date})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Description (optional)</Label>
                    <Input
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="e.g., Blood test results from Apollo"
                      data-testid="file-description-input"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)} className="flex-1">
                      {t('cancel')}
                    </Button>
                    <Button
                      onClick={handleFileUpload}
                      disabled={!selectedFile || uploading}
                      className="flex-1 bg-[#0D2340]"
                      data-testid="upload-submit-btn"
                    >
                      {uploading ? 'Encrypting...' : 'Upload & Encrypt'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Add Report Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0D2340]" data-testid="add-report-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addReport')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">{t('addReport')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>{t('reportType')}</Label>
                    <Select value={formData.report_type} onValueChange={(v) => setFormData({ ...formData, report_type: v })}>
                      <SelectTrigger data-testid="report-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {reportTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('reportName')}</Label>
                    <Input
                      value={formData.report_name}
                      onChange={(e) => setFormData({ ...formData, report_name: e.target.value })}
                      placeholder="e.g., Complete Blood Count"
                      required
                      data-testid="report-name-input"
                    />
                  </div>
                  <div>
                    <Label>{t('reportDate')}</Label>
                    <Input
                      type="date"
                      value={formData.report_date}
                      onChange={(e) => setFormData({ ...formData, report_date: e.target.value })}
                      required
                      data-testid="report-date-input"
                    />
                  </div>
                  <div>
                    <Label>{t('labName')}</Label>
                    <Input
                      value={formData.lab_name}
                      onChange={(e) => setFormData({ ...formData, lab_name: e.target.value })}
                      placeholder="e.g., Apollo Diagnostics"
                      data-testid="lab-name-input"
                    />
                  </div>
                  <div>
                    <Label>{t('findings')}</Label>
                    <Textarea
                      value={formData.findings}
                      onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                      placeholder="Key findings from the report..."
                      rows={4}
                      data-testid="findings-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="save-report-btn">
                      {t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Encrypted Files Section */}
        {files.length > 0 && (
          <Card className="bg-[#0D2340]/5 border-[#0D2340]/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-5 w-5 text-[#0D2340]" />
                <h3 className="font-semibold text-[#0D2340]">Encrypted Files ({files.length})</h3>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {files.slice(0, 4).map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                    <div className="flex items-center gap-3">
                      {file.content_type?.startsWith('image') ? (
                        <Image className="h-5 w-5 text-blue-500" />
                      ) : (
                        <File className="h-5 w-5 text-red-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium truncate max-w-[150px]">{file.original_filename}</p>
                        <p className="text-xs text-slate-500">{formatFileSize(file.file_size)}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(file.id, file.original_filename)}
                        data-testid={`download-file-${file.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteFile(file.id)}
                        className="text-red-500"
                        data-testid={`delete-file-${file.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('noData')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {entries.map((entry) => {
              const linkedFile = getFileForReport(entry.id);
              return (
                <Card key={entry.id} className="card-interactive" data-testid={`report-card-${entry.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${getReportIcon(entry.report_type)}`}>
                        <FileText className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg font-['Manrope']">{entry.report_name}</h3>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline">{getReportTypeLabel(entry.report_type)}</Badge>
                          <span className="text-sm text-slate-500">{entry.report_date}</span>
                          {linkedFile && (
                            <Badge className="bg-[#0D2340]/10 text-[#0D2340]">
                              <Lock className="h-3 w-3 mr-1" />
                              File attached
                            </Badge>
                          )}
                        </div>
                        {entry.lab_name && (
                          <p className="text-sm text-slate-500 mb-2">{t('labName')}: {entry.lab_name}</p>
                        )}
                        {entry.findings && (
                          <p className="text-sm text-slate-600 line-clamp-2">{entry.findings}</p>
                        )}
                        <div className="mt-3 flex items-center gap-2">
                          <Badge className="blockchain-badge text-xs">
                            <ShieldCheck className="h-3 w-3" />
                            {entry.blockchain_hash?.slice(0, 12)}...
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {linkedFile && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(linkedFile.id, linkedFile.original_filename)}
                            className="text-[#0D2340]"
                            data-testid={`download-report-file-${entry.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => speak(`${entry.report_name}. ${entry.findings || ''}`)}
                          data-testid={`speak-report-${entry.id}`}
                        >
                          <Volume2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-report-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
